import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import useFlexData from '../../hooks/flexHooks/useFlexData';
import { useLocation } from 'react-router-dom';

import { ThemeProvider } from 'styled-components';
import nebulaTheme from '@nebula/theme';
import { isMobileApp, MobileLocations, navigateTo } from '@ecs-uk/web-common-mobile-view-helpers';

import ThanksIndex from '.';
import ThankYou from '../ThankYou';
import getAllSubscriptions from '../../actions/subscriptions/getAll';
import getAllOffers from '../../actions/offers/getAll';
import getBillingData from '../../actions/billing/getBilling';
import Loading from '../../components/Loading';
import BILLING_PROVIDERS from '../../constants/billingProviders';
import { products } from '../../constants';
import { getHomeUrl } from '../../helpers';
import ProcessingSpinner from '../../components/PaymentProcessing/ProcessingSpinner';
import NewLoading from '../../components/CommonComponents/NewLoading';
import splitsList from '../../constants/splits';
import { formatRefundAmount } from '../../helpers/flex-helpers/paidAnnualIdentityToCreditExpert';

const hasStoreComponentRefreshed = (storeComponent, refreshRequestTime) =>
  storeComponent.lastRefreshed?.getTime() >= refreshRequestTime.getTime() || storeComponent.error;

const FLEX_THANK_YOU_PRODUCTS = new Set([products.identity, products.credit_expert]);

const formatPrice = price => (Number.isNaN(Number.parseInt(price, 10)) ? '' : `£${price}`);
const formatProRataPrice = price => (price !== null && price !== undefined ? `£${Number(price).toFixed(2)}` : null);

const ThanksLanding = ({ isWebview }) => {
  const [refreshRequestTime] = useState(new Date());

  const newSwitchJourney = useSelector(state => state.billing?.billingProvider === BILLING_PROVIDERS.NEW);
  const isFlexSplitEnabled = useSelector(state => state.split?.treatments?.[splitsList.isFlexSplitEnabled] === true);
  const { isNewDesignEnabled, isFlexUser: flexUser } = useFlexData();
  const showNewLoading = isNewDesignEnabled && (flexUser || isFlexSplitEnabled);

  const dispatch = useDispatch();
  const location = useLocation();

  /*
    The flex journey hands everything the new thank you page needs through the route state
    (see paymentCompleteTransition), so billing is the only dependency we need to refresh.
  */
  const flexThankYou = location.state?.flexThankYou;
  const showFlexThankYou = Boolean(flexThankYou) && FLEX_THANK_YOU_PRODUCTS.has(flexThankYou.product);
  /*
    ThanksIndex clears isValidRedirect (history.replace) once it has published its page-load analytics,
    as a guard against re-publishing on refresh. Reading it live here would make that clear-out look like
    a fresh deeplink, so capture it once on mount instead of rederiving it from location.state every render.
  */
  const isValidRedirectRef = useRef(Boolean(location.state?.isValidRedirect));
  const isValidRedirect = isValidRedirectRef.current;

  /*
    A flex user reaching this page without that route state (a refresh, or a deeplink) has nothing to
    be thanked for, so send them back to their subscription page rather than rendering an empty page.
    Legacy journeys (voucher redemption, DD card confirm, cooling-off) still land here with
    isValidRedirect and no flexThankYou - those are legitimate and should render the normal thanks page.
  */
  const isMissingFlexThankYouState = isNewDesignEnabled && flexUser && !flexThankYou && !isValidRedirect;

  useEffect(() => {
    if (isMissingFlexThankYouState) {
      dispatch(push('/'));
    }
  }, [dispatch, isMissingFlexThankYouState]);

  useEffect(() => {
    dispatch(getBillingData());

    if (showFlexThankYou) {
      return;
    }

    dispatch(getAllSubscriptions());
    dispatch(getAllOffers());
  }, [dispatch, showFlexThankYou]);

  const billingRefreshed = useSelector(state => hasStoreComponentRefreshed(state.billing, refreshRequestTime));
  const dependenciesRefreshed = useSelector(state => {
    const subscriptionsReady = hasStoreComponentRefreshed(state.subscriptions, refreshRequestTime);
    const offersReady = hasStoreComponentRefreshed(state.offers, refreshRequestTime);

    return subscriptionsReady && offersReady;
  });

  const nextBillingDate = useSelector(state => state.billing?.nextBillingDate);
  const proRataPrice = useSelector(state => state.proRataPrice?.estimatedPrice);

  const storeUpdated = showFlexThankYou ? billingRefreshed : billingRefreshed && dependenciesRefreshed;

  if (storeUpdated) {
    if (showFlexThankYou) {
      return (
        <ThankYou
          product={flexThankYou.product}
          previousProduct={flexThankYou.previousProduct}
          hasFreeTrial={Boolean(flexThankYou.hasFreeTrial)}
          isTargetAnnual={Boolean(flexThankYou.isTargetAnnual)}
          isCurrentAnnual={Boolean(flexThankYou.isCurrentAnnual)}
          price={formatPrice(flexThankYou.price)}
          proRataPrice={formatProRataPrice(proRataPrice)}
          refundAmount={formatRefundAmount(flexThankYou.previousPlanPrice)}
          nextBillingDate={nextBillingDate}
          onCtaClick={() => {
            if (isMobileApp()) {
              navigateTo(MobileLocations.SUMMARY);
              return;
            }
            globalThis.location.href = getHomeUrl(flexThankYou.product, flexThankYou.hasFreeTrial);
          }}
        />
      );
    }

    return <ThanksIndex />;
  }

  // Keeps the loading states consistent throughout old/new switch journeys
  return newSwitchJourney || showFlexThankYou ? (
    <ThemeProvider theme={nebulaTheme}>
      {showNewLoading ? <NewLoading isWebview={isWebview} /> : <ProcessingSpinner isWebview={false} enableText />}
    </ThemeProvider>
  ) : (
    <Loading solidBackground />
  );
};

ThanksLanding.propTypes = {
  isWebview: PropTypes.bool,
};

export default ThanksLanding;
